/**
 * Shared pickers for Meta resources used by ad set / ad / creative forms.
 *  - <FbPagePicker>          — choose a Facebook Page (avatar + name)
 *  - <FbPagesMultiPicker>    — authorize one or more Pages on the ad account
 *  - <IgAccountPicker>       — choose any IG business account the user manages
 *                              (independent of the selected Page); supports
 *                              "None — Facebook only".
 *  - <FbPixelPicker>         — pick a Meta Pixel for Sales/Conversions ad sets;
 *                              supports inline create.
 *  - <FbAppPicker>           — pick a Facebook App for Traffic-to-APP / App
 *                              Promotion ad sets; auto-fills the App Store
 *                              URL from Meta's metadata; opens a guided
 *                              register modal when the user has no apps.
 *  - <WhatsAppNumberPicker>  — local saved WA accounts, for WhatsApp-objective ad sets
 *
 * All call /slighter_crm/facebook/meta-resources/* — see meta-resources.service.ts.
 */
import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Link as RouterLink } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Camera, MessageSquare, Image as ImageIcon, AlertCircle, Ban, Check, Activity, Plus, Loader2, Smartphone, ExternalLink, RefreshCw, Info, X, Lock, Code, Copy } from 'lucide-react';
import { metaResourcesService, MetaPage, MetaInstagramAccount, MetaPixel, MetaApp, SaveMetaAppInput, WaNumber } from '@/services/meta-resources.service';
import { facebookAdAccountService } from '@/services/facebook-ad-account.service';

// ─── FbPagePicker ─────────────────────────────────────────────────────────────

export interface FbPagePickerProps {
  value: string;
  onChange: (pageId: string, page: MetaPage | null) => void;
  disabled?: boolean;
  /**
   * When true, the list is filtered to the ad account's authorized Pages
   * (`acc.pageIds`, falling back to legacy `acc.pageId`). Pages the user
   * manages on Meta but hasn't authorized in Ad Account Settings are hidden
   * — picking one would have caused the creative save to fail later, so
   * better to never offer the option. Defaults to `false` to preserve
   * behavior for any non-ad-set caller; ad-set forms pass `true`.
   */
  restrictToAuthorized?: boolean;
}

export const FbPagePicker: React.FC<FbPagePickerProps> = ({
  value,
  onChange,
  disabled,
  restrictToAuthorized,
}) => {
  const [pages, setPages] = useState<MetaPage[] | null>(null);
  const [authorized, setAuthorized] = useState<string[] | null>(restrictToAuthorized ? null : []);
  /**
   * Default Page ID from the ad account — used to auto-pre-select once the
   * Pages + authorized list have loaded if the parent didn't already pass
   * a `value`. Without this, every new ad set form starts the picker empty
   * even when the user has clearly chosen a default Page on Ad Account
   * Settings — that's the UX miss this fixes.
   */
  const [defaultPageId, setDefaultPageId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    metaResourcesService
      .pages()
      .then(p => { if (!cancelled) setPages(p); })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.message ?? 'Failed to load Pages');
      });
    return () => { cancelled = true; };
  }, []);

  // Always fetch the ad account so we know the user's default Page —
  // needed both for filtering (`restrictToAuthorized`) and for the
  // auto-prefill behavior. One round-trip either way.
  useEffect(() => {
    let cancelled = false;
    facebookAdAccountService.get()
      .then(acc => {
        if (cancelled) return;
        const ids = acc.pageIds && acc.pageIds.length > 0
          ? acc.pageIds
          : acc.pageId ? [acc.pageId] : [];
        if (restrictToAuthorized) setAuthorized(ids);
        // Pick the legacy default if it's in the authorized list, otherwise
        // the first authorized page. Either way the user can override.
        const seed = (acc.pageId && ids.includes(acc.pageId)) ? acc.pageId : ids[0];
        if (seed) setDefaultPageId(seed);
      })
      .catch(() => { if (!cancelled && restrictToAuthorized) setAuthorized([]); });
    return () => { cancelled = true; };
  }, [restrictToAuthorized]);

  // Auto-prefill once everything's loaded and the parent has no selection.
  // We only fire this when `value` is empty so we never override an
  // intentional choice the user already made (or that came from a draft).
  useEffect(() => {
    if (value) return;
    if (!defaultPageId) return;
    if (!pages || pages.length === 0) return;
    const seed = pages.find(p => p.id === defaultPageId);
    if (seed) onChange(seed.id, seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, defaultPageId]);

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-xs text-text-secondary">
        <AlertCircle size={14} className="mt-0.5 shrink-0 text-warning" />
        <span>{error}. Make sure your Meta token has <code>pages_read_engagement</code>.</span>
      </div>
    );
  }

  if (pages === null || (restrictToAuthorized && authorized === null)) {
    return <div className="skeleton h-10 rounded-lg" />;
  }

  // Filter to authorized when requested. When the account has zero authorized
  // pages, fall through to the empty-state below rather than silently showing
  // every Meta page (which would defeat the purpose of the filter).
  const visible = restrictToAuthorized && authorized
    ? pages.filter(p => authorized.includes(p.id))
    : pages;

  if (pages.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-surface-alt px-3 py-2.5 text-xs text-text-muted">
        No Pages found for this Meta account.
      </div>
    );
  }

  // Authorized list is empty (or filtered list is empty because nothing matches).
  if (restrictToAuthorized && visible.length === 0) {
    return (
      <div className="rounded-lg border border-warning/40 bg-warning-soft px-3 py-3 text-xs">
        <div className="flex items-start gap-2">
          <Lock size={13} className="mt-0.5 shrink-0 text-warning" />
          <div className="flex-1 text-text-secondary">
            <div className="font-semibold text-text-primary mb-0.5">No authorized Pages on this ad account</div>
            <div>
              You manage {pages.length} Page{pages.length === 1 ? '' : 's'} on Meta, but none are authorized
              for ad creation here. Authorize at least one in{' '}
              <RouterLink to="/facebook/ad-account" className="font-medium text-primary hover:underline">
                Ad Account Settings
              </RouterLink>{' '}
              before saving this ad set.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5 max-h-64 overflow-y-auto rounded-lg border border-border bg-surface-alt p-1.5">
      {visible.map(p => {
        const selected = p.id === value;
        return (
          <button
            key={p.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(p.id, p)}
            className={clsx(
              'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors disabled:opacity-60',
              selected
                ? 'bg-primary-soft border border-primary'
                : 'bg-surface hover:bg-surface-alt border border-transparent',
            )}
          >
            <div className="flex h-8 w-8 shrink-0 overflow-hidden rounded-full bg-surface-alt">
              {p.pictureUrl ? (
                <img src={p.pictureUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-text-muted">
                  <ImageIcon size={14} />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-text-primary">{p.name}</div>
              <div className="font-mono text-2xs text-text-muted">{p.id}</div>
            </div>
            {p.instagramAccount && (
              <Camera size={13} className="shrink-0 text-text-muted" aria-label="Has linked Instagram" />
            )}
          </button>
        );
      })}
    </div>
  );
};

// ─── FbPagesMultiPicker ───────────────────────────────────────────────────────
//
// Lets the user authorize one or more Facebook Pages on the ad account. The
// authorized list (`pageIds`) is what the workspace's per-ad-set page picker
// chooses from, and what the backend validates the creative's page against.

export interface FbPagesMultiPickerProps {
  value: string[];
  onChange: (pageIds: string[]) => void;
  disabled?: boolean;
}

export const FbPagesMultiPicker: React.FC<FbPagesMultiPickerProps> = ({ value, onChange, disabled }) => {
  const [pages, setPages] = useState<MetaPage[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    metaResourcesService
      .pages()
      .then(p => { if (!cancelled) setPages(p); })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.message ?? 'Failed to load Pages');
      });
    return () => { cancelled = true; };
  }, []);

  const toggle = (id: string) => {
    if (value.includes(id)) onChange(value.filter(x => x !== id));
    else onChange([...value, id]);
  };

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-xs text-text-secondary">
        <AlertCircle size={14} className="mt-0.5 shrink-0 text-warning" />
        <span>{error}. Make sure your Meta token has <code>pages_read_engagement</code>.</span>
      </div>
    );
  }

  if (pages === null) {
    return <div className="skeleton h-10 rounded-lg" />;
  }

  if (pages.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-surface-alt px-3 py-2.5 text-xs text-text-muted">
        No Pages found for this Meta account.
      </div>
    );
  }

  return (
    <div className="space-y-1.5 max-h-64 overflow-y-auto rounded-lg border border-border bg-surface-alt p-1.5">
      {pages.map(p => {
        const selected = value.includes(p.id);
        return (
          <button
            key={p.id}
            type="button"
            disabled={disabled}
            onClick={() => toggle(p.id)}
            className={clsx(
              'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors disabled:opacity-60',
              selected
                ? 'bg-primary-soft border border-primary'
                : 'bg-surface hover:bg-surface-alt border border-transparent',
            )}
          >
            <div
              className={clsx(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                selected ? 'border-primary bg-primary text-on-primary' : 'border-border bg-surface',
              )}
            >
              {selected && <Check size={12} />}
            </div>
            <div className="flex h-8 w-8 shrink-0 overflow-hidden rounded-full bg-surface-alt">
              {p.pictureUrl ? (
                <img src={p.pictureUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-text-muted">
                  <ImageIcon size={14} />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-text-primary">{p.name}</div>
              <div className="font-mono text-2xs text-text-muted">{p.id}</div>
            </div>
            {p.instagramAccount && (
              <Camera size={13} className="shrink-0 text-text-muted" aria-label="Has linked Instagram" />
            )}
          </button>
        );
      })}
    </div>
  );
};

// ─── IgAccountPicker ──────────────────────────────────────────────────────────
//
// Free picker: lists every IG business account the user manages, regardless of
// which Page is selected. Meta accepts any IG id the access token has rights
// over via `instagram_actor_id` — it doesn't have to be the IG linked to the
// page_id used in the ad set.
//
// `value === null` means "Facebook only" (no IG actor).

export interface IgAccountPickerProps {
  value: string | null;
  onChange: (igAccountId: string | null, account: MetaInstagramAccount | null) => void;
  /** When false, picker is greyed out and shows the explanation text. */
  disabled?: boolean;
  /** Optional override label that explains why the picker is disabled. */
  disabledReason?: string;
}

export const IgAccountPicker: React.FC<IgAccountPickerProps> = ({
  value,
  onChange,
  disabled,
  disabledReason,
}) => {
  const [accounts, setAccounts] = useState<MetaInstagramAccount[] | null>(null);
  const [defaultIgId, setDefaultIgId] = useState<string | null>(null);
  /**
   * Prevents the auto-prefill effect from firing twice. We only want to seed
   * the parent's `value` on the *first* mount when the parent has nothing
   * selected. If the user later clicks "None" (Facebook-only), `value`
   * becomes null again — without this guard, the effect would re-fire and
   * stomp their choice.
   */
  const prefillDone = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    metaResourcesService
      .instagramAccounts()
      .then(a => { if (!cancelled) setAccounts(a); })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.message ?? 'Failed to load Instagram accounts');
      });
    return () => { cancelled = true; };
  }, []);

  // Read the ad account's default IG so we can pre-select it.
  useEffect(() => {
    let cancelled = false;
    facebookAdAccountService.get()
      .then(acc => { if (!cancelled) setDefaultIgId(acc.instagramAccountId ?? null); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  // Auto-prefill once. Only fires when the parent has no selection yet
  // (`value === null` AND we haven't already prefilled). Picks the ad
  // account's default IG when it exists in the loaded list.
  useEffect(() => {
    if (prefillDone.current) return;
    if (value !== null) { prefillDone.current = true; return; }
    if (!accounts || accounts.length === 0) return;
    if (!defaultIgId) return;
    const seed = accounts.find(a => a.id === defaultIgId);
    if (seed) {
      prefillDone.current = true;
      onChange(seed.id, seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, defaultIgId]);

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-xs text-text-secondary">
        <AlertCircle size={14} className="mt-0.5 shrink-0 text-warning" />
        <span>{error}. Make sure your Meta token has <code>instagram_basic</code> + <code>pages_show_list</code>.</span>
      </div>
    );
  }

  if (accounts === null) {
    return <div className="skeleton h-10 rounded-lg" />;
  }

  const noneSelected = value === null;

  if (accounts.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-surface-alt px-3 py-2.5 text-xs text-text-muted">
        No Instagram business accounts found. Either link an IG to one of your
        Pages, or run Facebook-only ads.
      </div>
    );
  }

  return (
    <div className={clsx('space-y-1.5 max-h-64 overflow-y-auto rounded-lg border border-border bg-surface-alt p-1.5', disabled && 'opacity-60 pointer-events-none')}>
      {disabled && disabledReason && (
        <div className="rounded-md bg-surface px-2.5 py-1.5 text-2xs text-text-muted">
          {disabledReason}
        </div>
      )}

      {/* "None" option — always first */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(null, null)}
        className={clsx(
          'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors disabled:opacity-60',
          noneSelected
            ? 'bg-primary-soft border border-primary'
            : 'bg-surface hover:bg-surface-alt border border-transparent',
        )}
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface text-text-muted">
          <Ban size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-text-primary">None — Facebook only</div>
          <div className="text-2xs text-text-muted">Don&rsquo;t cross-post to Instagram</div>
        </div>
      </button>

      {accounts.map(a => {
        const selected = a.id === value;
        return (
          <button
            key={a.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(a.id, a)}
            className={clsx(
              'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors disabled:opacity-60',
              selected
                ? 'bg-primary-soft border border-primary'
                : 'bg-surface hover:bg-surface-alt border border-transparent',
            )}
          >
            <div className="flex h-8 w-8 shrink-0 overflow-hidden rounded-full bg-surface">
              {a.profilePictureUrl ? (
                <img src={a.profilePictureUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-text-muted">
                  <Camera size={14} />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-text-primary">
                @{a.username ?? a.id}
              </div>
              <div className="font-mono text-2xs text-text-muted">{a.id}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

// ─── FbPixelPicker ────────────────────────────────────────────────────────────
//
// Used by Sales/Conversions ad-set forms. Lists Meta Pixels on the connected
// ad account and supports creating a new one inline so users with no pixel
// yet aren't blocked. The picker shows last-fired-time so users can spot
// orphan pixels.

export interface FbPixelPickerProps {
  value: string;
  onChange: (pixelId: string, pixel: MetaPixel | null) => void;
  disabled?: boolean;
}

const formatLastFired = (iso?: string): string => {
  if (!iso) return 'Never fired';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days < 0) return 'Just now';
  if (days === 0) return 'Fired today';
  if (days === 1) return 'Fired yesterday';
  if (days < 30) return `Fired ${days}d ago`;
  if (days < 365) return `Fired ${Math.floor(days / 30)}mo ago`;
  return `Fired ${Math.floor(days / 365)}y ago`;
};

export const FbPixelPicker: React.FC<FbPixelPickerProps> = ({ value, onChange, disabled }) => {
  const [pixels, setPixels] = useState<MetaPixel[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [installFor, setInstallFor] = useState<MetaPixel | null>(null);

  const refresh = async () => {
    try {
      const list = await metaResourcesService.pixels();
      setPixels(list);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load Pixels');
    }
  };

  useEffect(() => {
    let cancelled = false;
    metaResourcesService
      .pixels()
      .then(p => { if (!cancelled) setPixels(p); })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.message ?? 'Failed to load Pixels');
      });
    return () => { cancelled = true; };
  }, []);

  const submitCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const created = await metaResourcesService.createPixel(name);
      toast.success(`Pixel "${created.name}" created`);
      setCreateOpen(false);
      setNewName('');
      await refresh();
      onChange(created.id, created);
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally {
      setCreating(false);
    }
  };

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-xs text-text-secondary">
        <AlertCircle size={14} className="mt-0.5 shrink-0 text-warning" />
        <span>{error}. Make sure your Meta token has <code>ads_management</code>.</span>
      </div>
    );
  }

  if (pixels === null) {
    return <div className="skeleton h-10 rounded-lg" />;
  }

  return (
    <>
    <div className={clsx('space-y-1.5', disabled && 'opacity-60 pointer-events-none')}>
      {pixels.length === 0 && !createOpen && (
        <div className="rounded-lg border border-border bg-surface-alt px-3 py-2.5 text-xs text-text-muted">
          No Pixels on this ad account yet. Click <strong>Create new Pixel</strong> below to make one.
        </div>
      )}

      {pixels.length > 0 && (
        <div className="space-y-1.5 max-h-64 overflow-y-auto rounded-lg border border-border bg-surface-alt p-1.5">
          {pixels.map(p => {
            const selected = p.id === value;
            const neverFired = !p.lastFiredTime;
            return (
              <div
                key={p.id}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={() => !disabled && onChange(p.id, p)}
                onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onChange(p.id, p); } }}
                className={clsx(
                  'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors',
                  disabled && 'opacity-60 pointer-events-none',
                  selected
                    ? 'bg-primary-soft border border-primary'
                    : 'bg-surface hover:bg-surface-alt border border-transparent cursor-pointer',
                )}
              >
                <div className={clsx(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                  p.lastFiredTime ? 'bg-success-soft text-success' : 'bg-surface-alt text-text-muted',
                )}>
                  <Activity size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-text-primary">{p.name || 'Unnamed Pixel'}</div>
                  <div className="flex items-center gap-1.5 text-2xs text-text-muted">
                    <span className="font-mono">{p.id}</span>
                    <span className="text-border-strong">·</span>
                    <span className={clsx(p.lastFiredTime ? 'text-success' : 'text-text-muted')}>
                      {formatLastFired(p.lastFiredTime)}
                    </span>
                  </div>
                </div>
                {neverFired && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setInstallFor(p); }}
                    className="flex shrink-0 items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-2xs font-medium text-text-secondary hover:border-primary hover:text-primary transition-colors"
                    title="Show install instructions for this pixel"
                  >
                    <Code size={11} />
                    How to install
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Inline create */}
      {createOpen ? (
        <div className="rounded-lg border border-border bg-surface p-2 space-y-2">
          <input
            autoFocus
            type="text"
            value={newName}
            disabled={creating}
            placeholder="e.g. Website Pixel"
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') submitCreate();
              if (e.key === 'Escape') { setCreateOpen(false); setNewName(''); }
            }}
            className="w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm focus:border-primary focus:outline-none"
          />
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              disabled={creating}
              onClick={() => { setCreateOpen(false); setNewName(''); }}
              className="rounded-md px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-alt"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={creating || newName.trim().length < 2}
              onClick={submitCreate}
              className="flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-on-primary disabled:opacity-50"
            >
              {creating ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
              Create
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setCreateOpen(true)}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border bg-surface px-3 py-2 text-xs font-medium text-text-secondary hover:border-primary hover:text-primary transition-colors"
        >
          <Plus size={12} />
          Create new Pixel
        </button>
      )}
    </div>
    {installFor && (
      <PixelInstallGuide pixel={installFor} onClose={() => setInstallFor(null)} />
    )}
    </>
  );
};

// ─── PixelInstallGuide (modal) ────────────────────────────────────────────────
//
// Shown when a freshly-created (or never-fired) pixel needs a website install.
// We hand the user a copy-pasteable Meta Pixel base code with their actual
// pixel ID baked in, plus install steps and a verification path. Without this,
// users with no developer have to leave the CRM and navigate Meta Events
// Manager to find the same code.

const PixelInstallGuide: React.FC<{ pixel: MetaPixel; onClose: () => void }> = ({ pixel, onClose }) => {
  const [copied, setCopied] = useState(false);

  // Meta's standard pixel base snippet. We swap the {ID} marker for the
  // user's actual numeric pixel id. PageView is auto-tracked once installed.
  const snippet = `<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${pixel.id}');
fbq('track', 'PageView');
</script>
<noscript><img height="1" width="1" style="display:none"
src="https://www.facebook.com/tr?id=${pixel.id}&ev=PageView&noscript=1"
/></noscript>
<!-- End Meta Pixel Code -->`;

  const purchaseSnippet = `// Fire this on the order-confirmation page
fbq('track', 'Purchase', {
  value: 99.99,                  // order total
  currency: 'AED',
});`;

  const leadSnippet = `// Fire this when a user submits a lead form
fbq('track', 'Lead');`;

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[680px] max-h-[90vh] overflow-y-auto rounded-2xl bg-surface shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-surface px-5 py-3">
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Install your Meta Pixel</h3>
            <p className="text-2xs text-text-muted">Copy this code into the &lt;head&gt; of every page on your site.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt"
          >
            <X size={15} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4 text-sm">
          <div className="flex items-start gap-2 rounded-lg border border-info/30 bg-info-soft px-3 py-2 text-xs">
            <Info size={13} className="mt-0.5 shrink-0 text-info" />
            <div className="text-text-secondary">
              <strong className="text-text-primary">Pixel ID:</strong>{' '}
              <span className="font-mono">{pixel.id}</span> · already baked into the snippet below.
              Once it's installed and you visit any page on your site, the pixel will fire and the
              status here turns from <em>Never fired</em> to <em>Fired today</em>.
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-text-primary">1. Base code (required)</h4>
              <button
                type="button"
                onClick={() => copy(snippet)}
                className="flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-2xs font-medium text-text-secondary hover:border-primary hover:text-primary"
              >
                {copied ? <Check size={11} /> : <Copy size={11} />}
                {copied ? 'Copied' : 'Copy code'}
              </button>
            </div>
            <pre className="rounded-lg border border-border bg-surface-alt p-3 text-2xs text-text-primary overflow-x-auto whitespace-pre">
              {snippet}
            </pre>
            <p className="mt-2 text-2xs text-text-muted">
              Paste this just before <code>&lt;/head&gt;</code> on every page. PageView fires
              automatically — you don't need to add anything else for basic tracking.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-text-primary mb-2">2. Sales / Conversions events (optional, but recommended)</h4>
            <div className="space-y-2">
              <details className="rounded-lg border border-border bg-surface">
                <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-text-secondary">
                  Track a Purchase
                </summary>
                <div className="border-t border-border p-3 space-y-2">
                  <pre className="rounded-md bg-surface-alt p-2 text-2xs text-text-primary overflow-x-auto">
                    {purchaseSnippet}
                  </pre>
                  <button
                    type="button"
                    onClick={() => copy(purchaseSnippet)}
                    className="text-2xs text-primary hover:underline"
                  >
                    Copy snippet
                  </button>
                </div>
              </details>
              <details className="rounded-lg border border-border bg-surface">
                <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-text-secondary">
                  Track a Lead
                </summary>
                <div className="border-t border-border p-3 space-y-2">
                  <pre className="rounded-md bg-surface-alt p-2 text-2xs text-text-primary overflow-x-auto">
                    {leadSnippet}
                  </pre>
                  <button
                    type="button"
                    onClick={() => copy(leadSnippet)}
                    className="text-2xs text-primary hover:underline"
                  >
                    Copy snippet
                  </button>
                </div>
              </details>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-text-primary mb-1">3. Verify it's working</h4>
            <p className="text-xs text-text-secondary">
              Install Meta's free <a
                href="https://chromewebstore.google.com/detail/meta-pixel-helper/fdgfkebogiimcoedlicjlajpkdmockpc"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 font-medium text-primary hover:underline"
              >
                Pixel Helper Chrome extension <ExternalLink size={10} />
              </a> and visit your site. A green badge means the pixel fired. You can also check{' '}
              <a
                href={`https://www.facebook.com/events_manager2/list/pixel/${pixel.id}/test_events`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 font-medium text-primary hover:underline"
              >
                Events Manager → Test events <ExternalLink size={10} />
              </a>.
            </p>
          </div>

          <div className="flex justify-end border-t border-border pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-on-primary"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── FbAppPicker ──────────────────────────────────────────────────────────────
//
// Picker for Facebook Apps the user develops (the `application_id` field on
// promoted_object for Traffic→APP and App Promotion ad sets). Lists apps from
// /me/applications/developer.
//
// Empty state opens a guided walkthrough modal with deep links to
// developers.facebook.com — Meta does NOT expose an API to programmatically
// create apps for non-Tech-Provider companies (the App Management API is
// restricted), so we explicitly walk the user through the manual flow rather
// than pretending we can do it for them.

export interface FbAppPickerProps {
  value: string;
  onChange: (appId: string, app: MetaApp | null) => void;
  /** Restrict the list to apps that have at least one app-store URL set
   *  (i.e. are registered as mobile apps and eligible for App Promotion). */
  mobileOnly?: boolean;
  disabled?: boolean;
}

export const FbAppPicker: React.FC<FbAppPickerProps> = ({ value, onChange, mobileOnly, disabled }) => {
  const [apps, setApps] = useState<MetaApp[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const list = await metaResourcesService.apps();
      setApps(list);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load apps');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDelete = async (app: MetaApp, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!app.isManual) return;
    if (!window.confirm(`Remove "${app.name}" from your saved apps?`)) return;
    try {
      await metaResourcesService.deleteApp(app.id);
      setApps(prev => prev?.filter(a => a.id !== app.id) ?? null);
      if (value === app.id) onChange('', null);
      toast.success('App removed');
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Failed to remove app');
    }
  };

  useEffect(() => {
    let cancelled = false;
    metaResourcesService
      .apps()
      .then(a => { if (!cancelled) setApps(a); })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.message ?? 'Failed to load apps');
      });
    return () => { cancelled = true; };
  }, []);

  const eligible = (apps ?? []).filter(a => {
    if (!mobileOnly) return true;
    const u = a.objectStoreUrls;
    return !!(u?.iphone_app_store || u?.google_play || u?.amazon_app_store);
  });

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-xs text-text-secondary">
        <AlertCircle size={14} className="mt-0.5 shrink-0 text-warning" />
        <span>{error}.</span>
      </div>
    );
  }

  if (apps === null) return <div className="skeleton h-10 rounded-lg" />;

  const showEmpty = eligible.length === 0;

  return (
    <>
      <div className={clsx('space-y-1.5', disabled && 'opacity-60 pointer-events-none')}>
        {showEmpty ? (
          <div className="rounded-lg border border-dashed border-border bg-surface-alt px-3 py-4 text-xs text-text-muted">
            <div className="font-medium text-text-primary mb-0.5">
              No apps in this list yet
            </div>
            <div>
              Meta no longer auto-syncs apps from developers.facebook.com. If you already
              have an app there, click <strong>Add my app</strong> below and paste its App
              ID. Otherwise, the walkthrough will get you set up.
            </div>
          </div>
        ) : (
          <div className="space-y-1.5 max-h-64 overflow-y-auto rounded-lg border border-border bg-surface-alt p-1.5">
            {eligible.map(a => {
              const selected = a.id === value;
              const stores = a.objectStoreUrls;
              return (
                <div
                  key={a.id}
                  className={clsx(
                    'group flex items-stretch rounded-md transition-colors',
                    selected
                      ? 'bg-primary-soft border border-primary'
                      : 'bg-surface hover:bg-surface-alt border border-transparent',
                  )}
                >
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(a.id, a)}
                    className="flex flex-1 items-center gap-2.5 px-2.5 py-2 text-left disabled:opacity-60"
                  >
                    <div className="flex h-8 w-8 shrink-0 overflow-hidden rounded-lg bg-surface-alt">
                      {a.iconUrl ? (
                        <img src={a.iconUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-text-muted">
                          <Smartphone size={14} />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-semibold text-text-primary">{a.name || 'Unnamed app'}</span>
                        {a.isManual && (
                          <span className="rounded bg-surface-alt px-1 py-px text-[9px] font-semibold uppercase tracking-wider text-text-muted">
                            Saved
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-2xs text-text-muted">
                        <span className="font-mono">{a.id}</span>
                        {stores?.iphone_app_store && (
                          <span className="rounded bg-surface px-1 py-px text-[9px] font-semibold text-text-secondary">iOS</span>
                        )}
                        {stores?.google_play && (
                          <span className="rounded bg-surface px-1 py-px text-[9px] font-semibold text-text-secondary">Android</span>
                        )}
                      </div>
                    </div>
                  </button>
                  {a.isManual && (
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={(e) => handleDelete(a, e)}
                      title="Remove this app"
                      className="flex w-8 shrink-0 items-center justify-center rounded-r-md text-text-muted opacity-0 group-hover:opacity-100 hover:bg-danger-soft hover:text-danger transition-all"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex gap-1.5">
          <button
            type="button"
            disabled={disabled}
            onClick={() => setManualOpen(true)}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-dashed border-primary/60 bg-primary-soft/30 px-3 py-2 text-xs font-semibold text-primary hover:bg-primary-soft transition-colors"
          >
            <Plus size={12} />
            Add my app
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setGuideOpen(true)}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-dashed border-border bg-surface px-3 py-2 text-xs font-medium text-text-secondary hover:border-primary hover:text-primary transition-colors"
          >
            Don't have one? Register
          </button>
          <button
            type="button"
            disabled={disabled || refreshing}
            onClick={refresh}
            title="Refresh app list"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-surface text-text-secondary hover:bg-surface-alt"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {guideOpen && (
        <RegisterAppGuide
          mobileOnly={mobileOnly}
          onClose={() => setGuideOpen(false)}
          onRefresh={async () => {
            await refresh();
          }}
        />
      )}

      {manualOpen && (
        <ManualAppForm
          mobileOnly={mobileOnly}
          onClose={() => setManualOpen(false)}
          onSaved={async (saved) => {
            setManualOpen(false);
            await refresh();
            onChange(saved.id, saved);
          }}
        />
      )}
    </>
  );
};

// ─── RegisterAppGuide (modal) ─────────────────────────────────────────────────
//
// Step-by-step walkthrough that gets a non-IT user from "I have no app" to
// "my app is registered and visible in the picker", without us pretending to
// have an API we don't (Meta gates app creation behind manual sign-up at
// developers.facebook.com).

interface RegisterAppGuideProps {
  mobileOnly?: boolean;
  onClose: () => void;
  onRefresh: () => Promise<void>;
}

const RegisterAppGuide: React.FC<RegisterAppGuideProps> = ({ mobileOnly, onClose, onRefresh }) => {
  const [busyRefresh, setBusyRefresh] = useState(false);
  const handleRefresh = async () => {
    setBusyRefresh(true);
    try { await onRefresh(); toast.success('App list refreshed'); }
    finally { setBusyRefresh(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[640px] max-h-[90vh] overflow-y-auto rounded-2xl bg-surface shadow-elevated"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-surface px-5 py-3">
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Register a Facebook App</h3>
            <p className="text-2xs text-text-muted">A one-time setup at developers.facebook.com.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt"
          >
            <X size={15} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4 text-sm">
          <div className="flex items-start gap-2 rounded-lg border border-info/30 bg-info-soft px-3 py-2 text-xs">
            <Info size={13} className="mt-0.5 shrink-0 text-info" />
            <div className="text-text-secondary">
              <strong className="text-text-primary">Why we can't do this for you:</strong>{' '}
              Meta only allows apps to be created from <code>developers.facebook.com</code> by a real
              human signing in. There is no public API for it. Follow the steps below — it takes ~3 minutes.
            </div>
          </div>

          <ol className="space-y-3">
            <Step
              n={1}
              title="Open Meta for Developers"
              body={
                <>
                  Sign in with the same Facebook account that owns your ad account.
                </>
              }
              cta={{ label: 'Open developers.facebook.com', href: 'https://developers.facebook.com/apps/' }}
            />
            <Step
              n={2}
              title="Click Create App"
              body={
                <>
                  Top right of the Apps page. If it asks for "Use Case", pick{' '}
                  <strong>{mobileOnly ? 'Other' : 'Authenticate and request data from users with Facebook Login'}</strong>{' '}
                  — you can change this later. App type:{' '}
                  <strong>{mobileOnly ? 'Consumer' : 'Business'}</strong>.
                </>
              }
            />
            <Step
              n={3}
              title="Name your app"
              body={
                <>
                  Pick a name your customers won't see (it's only for ads). E.g. <em>"My Brand — Ads App"</em>.
                  Save and continue.
                </>
              }
            />
            {mobileOnly && (
              <Step
                n={4}
                title="Add iOS / Android platform"
                body={
                  <>
                    In the new app's left sidebar: <strong>Settings → Basic → Add Platform</strong>{' '}
                    → choose iOS and/or Android. Paste your Bundle ID (iOS) or Package Name (Android)
                    and the App Store / Play Store URL. <strong>This step is what makes the app show
                    up in the App Promotion picker.</strong>
                  </>
                }
              />
            )}
            <Step
              n={mobileOnly ? 5 : 4}
              title="Switch the app to Live"
              body={
                <>
                  Top of the app's page, toggle <strong>App Mode: Development → Live</strong>. You may
                  need to provide a privacy-policy URL.
                </>
              }
            />
            <Step
              n={mobileOnly ? 6 : 5}
              title="Come back here and refresh"
              body={
                <>
                  Once the app is created, click the button below — your new app should appear in the picker.
                </>
              }
            />
          </ol>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
            <a
              href="https://developers.facebook.com/docs/development/create-an-app"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              Meta's official guide <ExternalLink size={11} />
            </a>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-alt"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={busyRefresh}
                className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-on-primary disabled:opacity-50"
              >
                {busyRefresh ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                I've created my app — refresh
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Step: React.FC<{
  n: number;
  title: string;
  body: React.ReactNode;
  cta?: { label: string; href: string };
}> = ({ n, title, body, cta }) => (
  <li className="flex gap-3">
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-bold text-primary">
      {n}
    </span>
    <div className="min-w-0 flex-1 space-y-1">
      <div className="text-xs font-semibold text-text-primary">{title}</div>
      <div className="text-xs leading-relaxed text-text-secondary">{body}</div>
      {cta && (
        <a
          href={cta.href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          {cta.label} <ExternalLink size={10} />
        </a>
      )}
    </div>
  </li>
);

// ─── ManualAppForm (modal) ────────────────────────────────────────────────────
//
// Lets the user paste their Facebook App ID + store URLs when Meta's deprecated
// /me/applications/developer edge can't list their apps. Saves to our backend
// so they only do this once per app.

interface ManualAppFormProps {
  mobileOnly?: boolean;
  onClose: () => void;
  onSaved: (saved: MetaApp) => void;
}

const ManualAppForm: React.FC<ManualAppFormProps> = ({ mobileOnly, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [appId, setAppId] = useState('');
  const [appIdPrefilled, setAppIdPrefilled] = useState(false);
  const [googlePlayUrl, setGooglePlayUrl] = useState('');
  const [iphoneAppStoreUrl, setIphoneAppStoreUrl] = useState('');
  const [amazonAppStoreUrl, setAmazonAppStoreUrl] = useState('');
  const [showAmazon, setShowAmazon] = useState(false);
  const [busy, setBusy] = useState(false);

  // Pre-fill the App ID from the connected Meta Ad Account. For most setups
  // this IS the user's mobile app (single app registered at
  // developers.facebook.com handling both OAuth and App Promotion). Users with
  // separate apps can edit the field.
  useEffect(() => {
    let cancelled = false;
    facebookAdAccountService
      .get()
      .then(acc => {
        if (cancelled || !acc?.appId) return;
        setAppId(acc.appId);
        setAppIdPrefilled(true);
        if (!acc.facebookUserName) return;
        // If we don't already have a label, suggest one based on the FB user.
        setName(prev => prev || `${acc.facebookUserName} App`);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const appIdValid = /^\d{6,20}$/.test(appId.trim());
  const nameValid = name.trim().length >= 1;
  const hasAtLeastOneStore = mobileOnly
    ? !!(googlePlayUrl.trim() || iphoneAppStoreUrl.trim() || amazonAppStoreUrl.trim())
    : true;
  const canSubmit = appIdValid && nameValid && hasAtLeastOneStore && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload: SaveMetaAppInput = {
        appId: appId.trim(),
        name: name.trim(),
        googlePlayUrl: googlePlayUrl.trim() || undefined,
        iphoneAppStoreUrl: iphoneAppStoreUrl.trim() || undefined,
        amazonAppStoreUrl: amazonAppStoreUrl.trim() || undefined,
      };
      const saved = await metaResourcesService.saveApp(payload);
      toast.success('App saved');
      onSaved(saved);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Failed to save app');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[520px] max-h-[90vh] overflow-y-auto rounded-2xl bg-surface shadow-elevated"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-surface px-5 py-3">
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Add my Facebook App</h3>
            <p className="text-2xs text-text-muted">
              Paste your App ID from developers.facebook.com — saved here so you only do this once.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt"
          >
            <X size={15} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4 text-sm">
          <div className="flex items-start gap-2 rounded-lg border border-info/30 bg-info-soft px-3 py-2 text-xs">
            <Info size={13} className="mt-0.5 shrink-0 text-info" />
            <div className="text-text-secondary">
              <strong className="text-text-primary">Where do I find my App ID?</strong>{' '}
              Open{' '}
              <a
                href="https://developers.facebook.com/apps/"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                developers.facebook.com/apps
              </a>
              , click your app, and copy the 16-digit number at the top (next to the app name).
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium text-text-secondary">
              Label <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. SlightorCRM Android"
              maxLength={100}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-2xs text-text-muted">Internal name to recognize this app in pickers.</p>
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium text-text-secondary">
              App ID <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={appId}
              onChange={e => {
                setAppId(e.target.value.replace(/\D/g, '').slice(0, 20));
                setAppIdPrefilled(false);
              }}
              placeholder="1234567890123456"
              inputMode="numeric"
              className={clsx(
                'w-full rounded-lg border bg-surface px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2',
                appId && !appIdValid
                  ? 'border-danger focus:ring-danger'
                  : 'border-border focus:ring-primary',
              )}
            />
            {appIdPrefilled && appIdValid && (
              <p className="text-2xs text-text-muted">
                Pre-filled from your connected Facebook integration. Change if you registered a different app for mobile promotion.
              </p>
            )}
            {appId && !appIdValid && (
              <p className="text-2xs text-danger">App ID must be 6–20 digits.</p>
            )}
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium text-text-secondary">Google Play URL</label>
            <input
              type="url"
              value={googlePlayUrl}
              onChange={e => setGooglePlayUrl(e.target.value)}
              placeholder="https://play.google.com/store/apps/details?id=…"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium text-text-secondary">App Store URL (iOS)</label>
            <input
              type="url"
              value={iphoneAppStoreUrl}
              onChange={e => setIphoneAppStoreUrl(e.target.value)}
              placeholder="https://apps.apple.com/app/id…"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {!showAmazon ? (
            <button
              type="button"
              onClick={() => setShowAmazon(true)}
              className="text-2xs font-medium text-primary hover:underline"
            >
              + Add Amazon Appstore URL
            </button>
          ) : (
            <div className="space-y-1">
              <label className="block text-xs font-medium text-text-secondary">Amazon Appstore URL</label>
              <input
                type="url"
                value={amazonAppStoreUrl}
                onChange={e => setAmazonAppStoreUrl(e.target.value)}
                placeholder="https://www.amazon.com/dp/…"
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          )}

          {mobileOnly && !hasAtLeastOneStore && (name || appId) && (
            <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-xs text-text-secondary">
              <AlertCircle size={13} className="mt-0.5 shrink-0 text-warning" />
              <span>Add at least one store URL — App Promotion ad sets need it.</span>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-border bg-surface px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-alt"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-on-primary disabled:opacity-50"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
            Save app
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── WhatsAppNumberPicker ─────────────────────────────────────────────────────

export interface WhatsAppNumberPickerProps {
  value: string;
  onChange: (phoneNumberId: string, num: WaNumber | null) => void;
  disabled?: boolean;
}

export const WhatsAppNumberPicker: React.FC<WhatsAppNumberPickerProps> = ({ value, onChange, disabled }) => {
  const [nums, setNums] = useState<WaNumber[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    metaResourcesService
      .whatsappNumbers()
      .then(n => { if (!cancelled) setNums(n); })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.message ?? 'Failed to load WhatsApp numbers');
      });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-xs text-text-secondary">
        <AlertCircle size={14} className="mt-0.5 shrink-0 text-warning" />
        <span>{error}</span>
      </div>
    );
  }

  if (nums === null) {
    return <div className="skeleton h-10 rounded-lg" />;
  }

  if (nums.length === 0) {
    return (
      <div className="flex items-start gap-2.5 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2.5 text-xs text-text-secondary">
        <AlertCircle size={14} className="mt-0.5 shrink-0 text-warning" />
        <div className="flex-1">
          <div className="font-semibold text-text-primary">No WhatsApp accounts connected</div>
          <p className="mt-0.5 text-text-muted">
            To run ads that click-to-WhatsApp, connect a WhatsApp business
            number first. You can do that from WhatsApp Settings.
          </p>
          <a
            href="/whatsapp/accounts"
            target="_blank"
            rel="noreferrer"
            className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
          >
            Connect WhatsApp →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5 rounded-lg border border-border bg-surface-alt p-1.5">
      {nums.map(n => {
        const selected = n.phoneNumberId === value;
        return (
          <button
            key={n.phoneNumberId}
            type="button"
            disabled={disabled}
            onClick={() => onChange(n.phoneNumberId, n)}
            className={clsx(
              'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors disabled:opacity-60',
              selected
                ? 'bg-primary-soft border border-primary'
                : 'bg-surface hover:bg-surface-alt border border-transparent',
            )}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface text-text-muted">
              <MessageSquare size={14} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-text-primary">
                {n.verifiedName}
              </div>
              <div className="font-mono text-2xs text-text-muted">{n.displayPhoneNumber}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

// ─── PlacementPicker ──────────────────────────────────────────────────────────
//
// Maps directly onto Meta's targeting placement fields:
//   publisher_platforms, facebook_positions, instagram_positions,
//   audience_network_positions, messenger_positions, device_platforms.
//
// Modes:
//   - 'automatic' — clear all six fields and let Meta pick.
//   - 'manual'    — render platform checkboxes + per-platform sub-position
//                   checkboxes (collapsed by default, expanded via "Customize").
//
// Sub-position validity (e.g. Reels-only on IG) is left to Meta — we just pass
// through what the user selects.

export interface PlacementValue {
  mode: 'automatic' | 'manual';
  publisher_platforms?: string[];
  facebook_positions?: string[];
  instagram_positions?: string[];
  audience_network_positions?: string[];
  messenger_positions?: string[];
  device_platforms?: string[];
}

export interface PlacementPickerProps {
  value: PlacementValue;
  onChange: (v: PlacementValue) => void;
  disabled?: boolean;
  /**
   * When set, only this platform's positions are shown and the platform
   * selection is locked — used by the Instagram workspace to prevent
   * accidental Facebook / Audience Network / Messenger placements. The
   * picker is auto-switched to `'manual'` mode and `publisher_platforms`
   * is forced to `[lockedPlatform]` on mount.
   */
  lockedPlatform?: 'facebook' | 'instagram';
}

export function defaultPlacement(): PlacementValue {
  return { mode: 'automatic' };
}

const FB_POSITIONS = [
  { code: 'feed', label: 'Feed' },
  { code: 'right_hand_column', label: 'Right column' },
  { code: 'story', label: 'Stories' },
  { code: 'video_feeds', label: 'Video feeds' },
  { code: 'marketplace', label: 'Marketplace' },
  { code: 'search', label: 'Search' },
  { code: 'instream_video', label: 'In-stream video' },
];
const IG_POSITIONS = [
  { code: 'stream', label: 'Feed' },
  { code: 'story', label: 'Stories' },
  { code: 'explore', label: 'Explore' },
  { code: 'reels', label: 'Reels' },
];
const AN_POSITIONS = [
  { code: 'classic', label: 'Native, banner, interstitial' },
  { code: 'rewarded_video', label: 'Rewarded video' },
];
const MSG_POSITIONS = [
  { code: 'messenger_home', label: 'Inbox' },
  { code: 'story', label: 'Stories' },
];
const PLATFORMS: Array<{ code: 'facebook' | 'instagram' | 'audience_network' | 'messenger'; label: string; positions: { code: string; label: string }[]; field: keyof PlacementValue }> = [
  { code: 'facebook', label: 'Facebook', positions: FB_POSITIONS, field: 'facebook_positions' },
  { code: 'instagram', label: 'Instagram', positions: IG_POSITIONS, field: 'instagram_positions' },
  { code: 'audience_network', label: 'Audience Network', positions: AN_POSITIONS, field: 'audience_network_positions' },
  { code: 'messenger', label: 'Messenger', positions: MSG_POSITIONS, field: 'messenger_positions' },
];

export const PlacementPicker: React.FC<PlacementPickerProps> = ({ value, onChange, disabled, lockedPlatform }) => {
  // Auto-expand the locked-platform's positions panel so the user sees
  // Feed/Reels/Stories/Explore (or FB feed/right column/etc.) without a
  // second click. Other platforms stay collapsed until clicked.
  const [expanded, setExpanded] = useState<Record<string, boolean>>(
    lockedPlatform ? { [lockedPlatform]: true } : {},
  );

  const platforms = value.publisher_platforms ?? [];

  // When the picker is locked to a single platform (e.g. Instagram-only
  // workspace), force-set the picker state on mount so the saved targeting
  // payload always carries the correct `publisher_platforms` even if the
  // user never touches the picker. Re-runs only when the lock or the value
  // shape changes — we never reset user-customised positions.
  useEffect(() => {
    if (!lockedPlatform) return;
    const needsForce =
      value.mode !== 'manual' ||
      !value.publisher_platforms ||
      value.publisher_platforms.length !== 1 ||
      value.publisher_platforms[0] !== lockedPlatform;
    if (needsForce) {
      onChange({
        mode: 'manual',
        publisher_platforms: [lockedPlatform],
        // Carry over any positions the user already picked for THIS platform,
        // drop anything for the other locked-out platforms so the payload
        // doesn't leak stale FB-only sub-positions on an IG ad set.
        instagram_positions: lockedPlatform === 'instagram' ? value.instagram_positions : undefined,
        facebook_positions: lockedPlatform === 'facebook' ? value.facebook_positions : undefined,
        device_platforms: value.device_platforms,
      });
    }
  }, [lockedPlatform, value, onChange]);

  // Display list — when locked, only the locked platform's positions render.
  const visiblePlatforms = lockedPlatform
    ? PLATFORMS.filter(p => p.code === lockedPlatform)
    : PLATFORMS;

  const setMode = (mode: 'automatic' | 'manual') => {
    if (lockedPlatform) return; // locked → manual is the only option
    if (mode === 'automatic') {
      onChange({ mode: 'automatic' });
    } else {
      // Default: all four platforms on, no sub-positions, all devices.
      onChange({
        mode: 'manual',
        publisher_platforms: ['facebook', 'instagram', 'audience_network', 'messenger'],
      });
    }
  };

  const togglePlatform = (code: string) => {
    if (lockedPlatform) return; // locked → platform list is fixed
    const set = new Set(platforms);
    if (set.has(code)) {
      if (set.size <= 1) return; // keep at least one
      set.delete(code);
    } else {
      set.add(code);
    }
    onChange({ ...value, publisher_platforms: Array.from(set) });
  };

  const toggleSubPosition = (field: keyof PlacementValue, code: string) => {
    const cur = (value[field] as string[] | undefined) ?? [];
    const next = cur.includes(code) ? cur.filter(p => p !== code) : [...cur, code];
    onChange({ ...value, [field]: next.length ? next : undefined });
  };

  const setDevices = (codes: string[]) => {
    onChange({ ...value, device_platforms: codes.length ? codes : undefined });
  };

  const devices = value.device_platforms ?? [];
  const deviceMode: 'all' | 'mobile' | 'desktop' =
    devices.length === 0 ? 'all'
    : devices.length === 1 && devices[0] === 'mobile' ? 'mobile'
    : devices.length === 1 && devices[0] === 'desktop' ? 'desktop'
    : 'all';

  return (
    <div className={clsx('space-y-3', disabled && 'opacity-60 pointer-events-none')}>
      {/* Mode toggle — hidden when the workspace has locked the picker to a
          single platform. There's no meaningful "automatic" choice when the
          surface is already pre-decided. */}
      {!lockedPlatform && (
        <div className="inline-flex rounded-lg border border-border bg-surface-alt p-0.5 gap-0.5">
          {[
            { v: 'automatic', l: 'Automatic placements (recommended)' },
            { v: 'manual', l: 'Manual placements' },
          ].map(o => (
            <button
              key={o.v}
              type="button"
              disabled={disabled}
              onClick={() => setMode(o.v as 'automatic' | 'manual')}
              className={clsx(
                'rounded-md px-3 py-1.5 text-xs font-medium transition-all',
                o.v === value.mode ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary',
              )}
            >
              {o.l}
            </button>
          ))}
        </div>
      )}

      {lockedPlatform && (
        <div className="rounded-lg border border-info/30 bg-info-soft px-3 py-2 text-2xs text-text-secondary">
          Placement is locked to <strong className="text-info uppercase">{lockedPlatform}</strong> for this workspace.
          Pick the {lockedPlatform === 'instagram' ? 'Instagram surfaces (Feed, Reels, Stories, Explore)' : 'Facebook surfaces'} you want — leave all unchecked to use every surface.
        </div>
      )}

      {value.mode === 'automatic' && !lockedPlatform ? (
        <p className="text-2xs text-text-muted">
          Meta will deliver your ads to the placements that are most likely to perform best.
        </p>
      ) : (
        <div className="space-y-2">
          {visiblePlatforms.map(p => {
            const checked = platforms.includes(p.code);
            const isExpanded = !!expanded[p.code];
            const subSelected = ((value[p.field] as string[] | undefined) ?? []);
            return (
              <div key={p.code} className="rounded-lg border border-border bg-surface-alt">
                <label className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary"
                    checked={checked}
                    disabled={disabled || !!lockedPlatform}
                    onChange={() => togglePlatform(p.code)}
                  />
                  <span className="flex-1 text-sm font-semibold text-text-primary">{p.label}</span>
                  {checked && (
                    <button
                      type="button"
                      onClick={e => { e.preventDefault(); setExpanded(s => ({ ...s, [p.code]: !s[p.code] })); }}
                      className="text-2xs font-medium text-primary hover:underline"
                    >
                      {isExpanded ? 'Hide positions' : `Customize positions${subSelected.length ? ` (${subSelected.length})` : ''}`}
                    </button>
                  )}
                </label>
                {checked && isExpanded && (
                  <div className="border-t border-border px-3 py-2 grid grid-cols-2 gap-1.5">
                    {p.positions.map(pos => (
                      <label key={pos.code} className="flex items-center gap-2 text-2xs text-text-secondary cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 accent-primary"
                          checked={subSelected.includes(pos.code)}
                          disabled={disabled}
                          onChange={() => toggleSubPosition(p.field, pos.code)}
                        />
                        {pos.label}
                      </label>
                    ))}
                    {subSelected.length === 0 && (
                      <p className="col-span-2 text-2xs text-text-muted italic">
                        None checked = all positions for this platform.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Device targeting */}
          <div className="flex items-center gap-2 pt-1">
            <span className="text-2xs font-medium text-text-secondary">Devices:</span>
            <div className="inline-flex rounded-lg border border-border bg-surface-alt p-0.5 gap-0.5">
              {[
                { v: 'all', l: 'All' },
                { v: 'mobile', l: 'Mobile' },
                { v: 'desktop', l: 'Desktop' },
              ].map(o => (
                <button
                  key={o.v}
                  type="button"
                  disabled={disabled}
                  onClick={() => setDevices(o.v === 'all' ? [] : [o.v])}
                  className={clsx(
                    'rounded-md px-3 py-1 text-2xs font-medium transition-all',
                    o.v === deviceMode ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary',
                  )}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Merge a PlacementValue into a targeting payload (in-place style — returns
 * a new object). Call after `buildTargetingPayload(...)` so we don't have to
 * touch the existing helper. When mode is 'automatic', no fields are added.
 */
export function applyPlacementToTargeting<T extends Record<string, unknown>>(
  targeting: T,
  placement: PlacementValue,
): T {
  if (placement.mode === 'automatic') return targeting;
  const out: Record<string, unknown> = { ...targeting };
  if (placement.publisher_platforms?.length) out.publisher_platforms = placement.publisher_platforms;
  if (placement.facebook_positions?.length) out.facebook_positions = placement.facebook_positions;
  if (placement.instagram_positions?.length) out.instagram_positions = placement.instagram_positions;
  if (
    placement.audience_network_positions?.length &&
    (!placement.publisher_platforms || placement.publisher_platforms.includes('audience_network'))
  ) {
    out.audience_network_positions = placement.audience_network_positions;
  }
  if (
    placement.messenger_positions?.length &&
    (!placement.publisher_platforms || placement.publisher_platforms.includes('messenger'))
  ) {
    out.messenger_positions = placement.messenger_positions;
  }
  if (placement.device_platforms?.length) out.device_platforms = placement.device_platforms;
  return out as T;
}
